// This is the method to create the edges (connection lines)
// pipelineItems is the map of flows, projects and resources

import { getColorFromItemStatus, getReferences } from './helpers';
import { resourceNameTitled } from 'js/components/pipelines/cards/utils';
import { PipelineItems } from './types';
import { labelBgStyles, labelStyles } from './consts';
import { DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE } from 'js/constants/config';
import { getRuntimeStatus } from 'js/lib/RuntimeStatusUtils';
import { Edge, MarkerType } from '@xyflow/react';
import { FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import { createSharedDataSetEdge } from './sharedDatasetHelpers';
import { hasDescendantSink } from './childrenHandlers';
import {
  buildMergedFlowDataForSchedule,
  filterWebhookResponseSourcesFromItems,
  getScheduleCanvasGraphKey,
  getScheduleCanvasVisibility,
  isPipelineSourceItem,
} from './utils';

// edges is the array of edges to be provided to react flow
export function createEdges(
  pipelineItems: PipelineItems,
  edges,
  {
    metaInfoById = {},
    nodeProps = {} as any,
    entities,
    edgesLinkMap = {},
    ragRequests = {},
    isSecondaryFlow = false,
    vendors = {} as Record<string, any>,
    canvasScheduleEdgesEnabled = false,
    scheduleCanvasFlowRootId = null as string | number | null,
    layoutDirection = 'LR' as 'LR' | 'TB',
    /**
     * ISSUE: Edge Creation Race Condition
     *
     *
     * Problem: Edges were being created before their target nodes existed, causing
     * edges with invalid targets like 'node-undefined' or edges that failed to render.
     *
     *
     * Solution: Pass a set of existing node IDs so createEdges() can validate that
     * both source and target nodes exist before creating edges.
     */
    existingNodeIds = new Set<string>(), // Set of node IDs that have been created
  },
): Edge[] {
  const filteredPipelineItems = filterWebhookResponseSourcesFromItems(pipelineItems);
  const { expandedNodes = [] } = nodeProps;
  const credentialEdgeIds = edges.reduce((acc, edge) => {
    acc[edge.id] = true;
    return acc;
  }, {});
  const scheduleCanvasEdgeIds = edges.reduce((acc, edge) => {
    if (typeof edge.id === 'string' && edge.id.startsWith('flow-control-edge-')) {
      acc[edge.id] = true;
    }
    return acc;
  }, {});

  Object.values(filteredPipelineItems).forEach((item) => {
    // Handle credential edges
    createCredentialEdge(item, edges, {
      expandedNodes,
      isSecondaryFlow,
      credentialEdgeIds,
      existingNodeIds,
    });

    createLastRunCanvasEdge(item, edges, {
      expandedNodes,
      isSecondaryFlow,
      existingNodeIds,
      scheduleCanvasEdgeIds,
      canvasScheduleEdgesEnabled,
      scheduleCanvasFlowRootId,
      vendors,
      metaInfoById,
      entities,
      layoutDirection,
      byoV2Enabled: nodeProps?.byoV2Enabled,
    });

    createCronJobCanvasEdge(item, edges, {
      expandedNodes,
      isSecondaryFlow,
      existingNodeIds,
      scheduleCanvasEdgeIds,
      canvasScheduleEdgesEnabled,
      scheduleCanvasFlowRootId,
      vendors,
      metaInfoById,
      entities,
      layoutDirection,
      byoV2Enabled: nodeProps?.byoV2Enabled,
    });

    /**
     * @Handle: Shared Dataset Edges
     */
    createSharedDataSetEdge(item, edges, {
      entities,
      existingNodeIds,
      expandedNodes,
      isSecondaryFlow,
    });

    /**
     * @Handle: BYO (custom) flow source → Code edge
     *
     * BYO flows render a synthetic Code node between the source and the sink
     * (see mappers/buildCodeNode). Always connect source → code; the code →
     * sink half is produced by the children loop below, which redirects the
     * edge's source from the source node to the code node for custom sources.
     */
    createCodeNodeEdge(item, edges, {
      expandedNodes,
      isSecondaryFlow,
      existingNodeIds,
      byoV2Enabled: nodeProps?.byoV2Enabled,
    });

    /**
     * @Handle: BYO (custom) flow Code → Destination edge
     *
     * The DATA_SET (nexset) is filtered from the rendered nodes for BYO sources
     * (mappers.ts skips it), so the normal children loop never reaches the
     * DATA_SINK. Instead, find every DATA_SINK whose children overlap with this
     * source's children (same nexset flow_node_id), and draw Code → Sink directly.
     * When no sink exists yet, draw Code → DestShell.
     */
    createByoCodeSinkEdges(item, edges, filteredPipelineItems, {
      expandedNodes,
      isSecondaryFlow,
      existingNodeIds,
      byoV2Enabled: nodeProps?.byoV2Enabled,
    });
    createByoCodeDestShellEdge(item, edges, filteredPipelineItems, {
      expandedNodes,
      isSecondaryFlow,
      existingNodeIds,
      byoV2Enabled: nodeProps?.byoV2Enabled,
    });

    if (item.retriever_flows) {
      item.retriever_flows.forEach((items: any) => {
        createEdges(items, edges, {
          metaInfoById,
          nodeProps,
          entities,
          edgesLinkMap,
          ragRequests,
          isSecondaryFlow,
          vendors,
          canvasScheduleEdgesEnabled,
          scheduleCanvasFlowRootId,
          layoutDirection,
          existingNodeIds, // Pass existingNodeIds to recursive call
        });
      });
    }

    if (item.items) {
      // This means item is a flow
      const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));
      checkForTriggeringFlows(item, edges, {
        nodeProps,
      });
      checkForFlowLinks(item, edges, {
        metaInfoById,
        entities,
        nodeProps,
        edgesLinkMap,
      });

      /**
       * Credential edges must be created even when the flow container is not
       * yet expanded.
       *
       * When a flow has linked / triggering flows, `SourcePipeline` wraps its
       * items under a container (`needsContainer = true`) and `createEdges`
       * iterates that container at the top level. The container itself is
       * never a SOURCE, so `createCredentialEdge` returns early on the
       * container; the source-with-credential lives one level deeper in
       * `item.items`. Before this pass, credential edges were only created
       * via the `if (expanded)` recursion below — so on the very first paint
       * the edge was missing until the user toggled layout direction, which
       * caused a full re-layout and (by coincidence) expanded the container.
       *
       * Credential *nodes* already render at canvas top level regardless of
       * container state; the guard inside `createCredentialEdge`
       * (`existingNodeIds` check) prevents orphan edges if a node somehow
       * isn't present. So it is safe to walk sub-items for credentials
       * unconditionally.
       */
      const subItems =
        item.items && typeof item.items === 'object' ? (Object.values(item.items) as any[]) : [];
      for (const subItem of subItems) {
        createCredentialEdge(subItem, edges, {
          expandedNodes,
          isSecondaryFlow,
          credentialEdgeIds,
          existingNodeIds,
        });
      }

      if (expanded) {
        /**
         * ISSUE: Missing Flow Items When Expanding Linked Flows
         *
         *
         * Problem: When expanding linked/secondary flows, items might not be loaded yet
         * because they're fetched asynchronously. Creating edges without items would:
         * - Create edges with invalid targets
         * - Cause errors when trying to access item.children
         * - Result in incomplete edge rendering
         *
         *
         * Solution: Check if items exist and are populated before creating edges.
         * If items are missing, skip edge creation and log a warning for debugging.
         * The items will be loaded by FlowNode/SecondaryFlowNode components when expanded.
         */
        const hasItems = item.items && Object.keys(item.items).length > 0;
        if (hasItems) {
          createEdges(item.items, edges, {
            metaInfoById,
            nodeProps,
            entities,
            edgesLinkMap,
            ragRequests,
            isSecondaryFlow,
            vendors,
            canvasScheduleEdgesEnabled,
            scheduleCanvasFlowRootId,
            layoutDirection,
            existingNodeIds, // Pass existingNodeIds to recursive call
          });
        } else {
          // Log a warning when items are missing for an expanded flow
          // This helps debug race conditions where items aren't ready yet
          console.warn(
            `[FlowNodes] Skipping edge creation for expanded flow ${item.flow_node_id || item.id}: items not ready yet`,
          );
        }
      }
      return;
    } else {
      // I'm also checking for triggering flows if we're creating edges for a resource
      // This is because a source also has flow_triggers, and when we are in listing mode, when we expand a flow we should show this connection
      checkForTriggeringFlows(item, edges, {
        nodeProps,
      });
    }

    const references = getReferences(item, entities);
    // Checking for references (resources referenced like lookups for sources)
    if (references?.length) {
      references.forEach((reference) => {
        const edgeStatus = 'RELATION';
        const edgeLabel = resourceNameTitled(reference);
        const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));

        const edge = {
          id: 'edge-' + edges.length,
          className: 'edge-status-' + edgeStatus,
          source: 'node-' + item.flow_node_id,
          target: 'node-' + reference.type + '-' + reference.id,
          selectable: false,
          deletable: false,
          focusable: false,
          reconnectable: false,
          label: edgeLabel,
          labelShowBg: true,
          labelBgBorderRadius: 4,
          labelBgPadding: [4, 4],
          labelBgStyle: labelBgStyles[edgeStatus],
          labelStyle: labelStyles[edgeStatus],
          zIndex: isSecondaryFlow && expanded ? 3001 : 2,
          targetHandle: 'top',
          sourceHandle: 'bottom',
        };
        edges.push(edge);
      });
    }

    if (item.children) {
      // If a resource has children, create the edge for each children
      const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));
      item.children.forEach((nodeId) => {
        let node = filteredPipelineItems[nodeId];

        // Webhook Source removed from pipelineItems; resolve from entities for Sink → Nexset edges
        if (
          !node &&
          item.type === 'DATA_SINK' &&
          item.node_type === 'data_sink' &&
          entities?.flowNodes
        ) {
          const webhookFlowNode = entities.flowNodes[nodeId];
          const connectorType =
            webhookFlowNode?.data_source_id != null
              ? entities?.sources?.[webhookFlowNode.data_source_id]?.source_type
              : webhookFlowNode?.source_type;
          const isIncomingWebhook =
            webhookFlowNode?.type === 'SOURCE' &&
            webhookFlowNode?.children?.length &&
            connectorType === 'nexla_rest';
          if (isIncomingWebhook) {
            webhookFlowNode.children.forEach((grandchildId: string) => {
              const grandchild = filteredPipelineItems[grandchildId];
              if (!grandchild?.flow_node_id) return;
              const sourceNodeId = 'node-' + item.flow_node_id;
              const targetNodeId = 'node-' + grandchild.flow_node_id;
              if (existingNodeIds.size > 0) {
                if (!existingNodeIds.has(sourceNodeId) || !existingNodeIds.has(targetNodeId))
                  return;
              }
              const runtimeStatus = DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE
                ? getRuntimeStatus(item, entities?.sources)
                : item.runtime_status;
              edges.push({
                id: 'edge-' + edges.length,
                className: 'edge-status-RELATION',
                source: sourceNodeId,
                target: targetNodeId,
                animated:
                  ragRequests[item.origin_node_id]?.loading ||
                  (runtimeStatus === 'PROCESSING' && item.status === 'ACTIVE'),
                selectable: false,
                deletable: false,
                focusable: false,
                reconnectable: false,
                label: 'API Response',
                labelShowBg: true,
                labelBgBorderRadius: 4,
                labelBgPadding: [4, 4],
                labelBgStyle: labelBgStyles['RELATION'],
                labelStyle: labelStyles['RELATION'],
                zIndex: isSecondaryFlow && expanded ? 3001 : 1,
                markerEnd: undefined,
                targetHandle: 'left',
                sourceHandle: 'right',
                data: { isWebhookEdge: true },
              });
            });
            return;
          }
        }

        /**
         * ISSUE: Missing Target Nodes in API Response
         *
         *
         * Problem: Sometimes the API response doesn't include all nodes that are
         * referenced in item.children. This can happen due to:
         * - Incomplete API responses
         * - Filtering/ACL restrictions
         * - Race conditions in data fetching
         *
         *
         * Solution: Validate that the target node exists before creating an edge.
         * Skip edge creation if the node is missing, and log a warning for debugging.
         */

        if (!node || !node.flow_node_id) {
          console.warn(
            `[FlowNodes] Skipping edge creation: target node ${nodeId} not found in pipelineItems`,
          );
          return;
        }

        const runtimeStatus = DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE
          ? getRuntimeStatus(item, entities?.sources)
          : item.runtime_status;
        const hasRelationOrDependency = (() => {
          return item.type === 'DATA_SINK' && item.children.length > 0;
        })();
        const edgeStatus = (() => {
          if (item.type === 'DATA_SINK') {
            return 'RELATION';
          }
          return item.status;
        })();
        const edgeLabel = (() => {
          if (item.type === 'DATA_SINK' && item.node_type === 'data_sink') {
            const displayName = metaInfoById[node.connector_type]?.displayName;
            return displayName === 'Incoming Webhook' ? 'API Response' : displayName;
          }
          return undefined;
        })();

        const canShowEdgeColor =
          item.node_type === item?.type?.toLowerCase() || item.node_type === 'data_source';

        /**
         * ISSUE: Edge Creation Before Target Nodes Exist
         *
         *
         * Problem: Edges were being created even when their target nodes didn't exist yet.
         * This happened because:
         * - Edge creation happens during the layout calculation
         * - Some nodes might not be created yet (e.g., in nested flows)
         * - Recursive edge creation can happen before all nodes are added
         *
         *
         * Solution: Validate that both source and target nodes exist in the existingNodeIds
         * set before creating the edge. This ensures edges are only created for nodes that
         * have already been created and added to the nodes array.
         */
        // For BYO (custom) flows the sink hangs off the synthetic Code node, not
        // the source directly, so reroute the edge's source to the code node.
        const isCustomSource =
          item.type === 'SOURCE' && item.flow_type === FLOW_TYPE_CUSTOM && nodeProps?.byoV2Enabled;
        const sourceNodeId = isCustomSource
          ? 'code-' + item.flow_node_id
          : 'node-' + item.flow_node_id;
        const targetNodeId = 'node-' + node?.flow_node_id;

        // Check if both nodes exist (if existingNodeIds is provided)
        if (existingNodeIds.size > 0) {
          if (!existingNodeIds.has(sourceNodeId) || !existingNodeIds.has(targetNodeId)) {
            if (!existingNodeIds.has(sourceNodeId) || !existingNodeIds.has(targetNodeId)) {
              // Skip edge creation if nodes don't exist yet
              // This prevents edges from appearing before nodes
              return;
            }
          }

          const edge = {
            id: 'edge-' + edges.length,
            className: canShowEdgeColor ? 'edge-status-' + edgeStatus : undefined,
            source: sourceNodeId,
            target: targetNodeId,
            animated:
              ragRequests[item.origin_node_id]?.loading ||
              (runtimeStatus === 'PROCESSING' && item.status === 'ACTIVE'),
            selectable: false,
            deletable: false,
            focusable: false,
            reconnectable: false,
            label: edgeLabel,
            labelShowBg: true,
            labelBgBorderRadius: 4,
            labelBgPadding: [4, 4],
            labelBgStyle: labelBgStyles[edgeStatus],
            labelStyle: labelStyles[edgeStatus],
            zIndex: isSecondaryFlow && expanded ? 3001 : 1,
            markerEnd: hasRelationOrDependency
              ? undefined
              : {
                  type: MarkerType.Arrow,
                  color: getColorFromItemStatus(canShowEdgeColor ? edgeStatus : 'ACTIVE'),
                  strokeWidth: 2,
                },
            targetHandle: 'left',
            sourceHandle: (() => {
              // for API server nodes connecting to request nodes, use method-specific handle
              if (item.node_type === 'api_server' && node?.node_type === 'api_request') {
                const method = node.endpoint_spec?.method;
                if (method) {
                  return `${method.toLowerCase()}-handle`;
                }
              }
              // for api_target nodes with custom success/fail handles
              if (item.node_type === 'api_target') {
                if (node?.node_type === 'api_result') {
                  return 'success';
                }
                if (node?.node_type === 'api_error') {
                  return 'fail';
                }
              }
              // Default logic for other node types
              return 'right';
            })(),
          };
          edges.push(edge);
        }
      });
    }
  });

  return edges;
}

type ScheduleCanvasEdgeOpts = {
  expandedNodes: string[];
  isSecondaryFlow: boolean;
  existingNodeIds: Set<string>;
  scheduleCanvasEdgeIds: Record<string, boolean>;
  canvasScheduleEdgesEnabled: boolean;
  scheduleCanvasFlowRootId: string | number | null;
  vendors: Record<string, any>;
  metaInfoById: Record<string, any>;
  entities?: { flowNodes?: Record<string, any>; sources?: Record<string, any> };
  layoutDirection: 'LR' | 'TB';
  byoV2Enabled?: boolean;
};

function pushScheduleCanvasFlowEdge(
  edges: Edge[],
  {
    edgeId,
    sourceId,
    targetId,
    sourceHandle,
    scheduleCanvasEdgeIds,
    existingNodeIds,
    isSecondaryFlow,
    expanded,
  }: {
    edgeId: string;
    sourceId: string;
    targetId: string;
    sourceHandle: string;
    scheduleCanvasEdgeIds: Record<string, boolean>;
    existingNodeIds: Set<string>;
    isSecondaryFlow: boolean;
    expanded: boolean;
  },
) {
  if (scheduleCanvasEdgeIds[edgeId]) {
    return;
  }
  if (
    existingNodeIds.size > 0 &&
    (!existingNodeIds.has(sourceId) || !existingNodeIds.has(targetId))
  ) {
    return;
  }

  edges.push({
    id: edgeId,
    source: sourceId,
    target: targetId,
    sourceHandle,
    targetHandle: 'flow-control-in',
    style: { strokeWidth: 1.5, stroke: 'var(--color-greyscale-600)' },
    animated: false,
    zIndex: isSecondaryFlow && expanded ? 3001 : 1,
    selectable: false,
    deletable: false,
    focusable: false,
    reconnectable: false,
  } as Edge);

  scheduleCanvasEdgeIds[edgeId] = true;
}

function createLastRunCanvasEdge(
  item: any,
  edges: Edge[],
  {
    expandedNodes,
    isSecondaryFlow,
    existingNodeIds,
    scheduleCanvasEdgeIds,
    canvasScheduleEdgesEnabled,
    scheduleCanvasFlowRootId,
    vendors,
    metaInfoById,
    entities,
    layoutDirection,
    byoV2Enabled,
  }: ScheduleCanvasEdgeOpts,
) {
  if (!canvasScheduleEdgesEnabled) {
    return;
  }

  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));
  if (
    !isPipelineSourceItem(item) ||
    item.node_type === 'api_target' ||
    (isSecondaryFlow && !expanded) ||
    item.flow_node_id == null
  ) {
    return;
  }

  const flowData = buildMergedFlowDataForSchedule(item, entities?.flowNodes, entities?.sources);
  const { shouldRenderRun } = getScheduleCanvasVisibility(
    flowData,
    vendors,
    metaInfoById,
    undefined,
    byoV2Enabled,
  );
  if (!shouldRenderRun) {
    return;
  }

  const graphKey = getScheduleCanvasGraphKey(item, scheduleCanvasFlowRootId);
  const runNodeId = `run-now-${graphKey}`;
  const targetId = `node-${item.flow_node_id}`;
  const edgeId = `flow-control-edge-${runNodeId}`;
  const sourceHandle = layoutDirection === 'TB' ? 'bottom' : 'right';

  pushScheduleCanvasFlowEdge(edges, {
    edgeId,
    sourceId: runNodeId,
    targetId,
    sourceHandle,
    scheduleCanvasEdgeIds,
    existingNodeIds,
    isSecondaryFlow,
    expanded,
  });
}

function createCronJobCanvasEdge(
  item: any,
  edges: Edge[],
  {
    expandedNodes,
    isSecondaryFlow,
    existingNodeIds,
    scheduleCanvasEdgeIds,
    canvasScheduleEdgesEnabled,
    scheduleCanvasFlowRootId,
    vendors,
    metaInfoById,
    entities,
    layoutDirection,
    byoV2Enabled,
  }: ScheduleCanvasEdgeOpts,
) {
  if (!canvasScheduleEdgesEnabled) {
    return;
  }

  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));
  if (
    !isPipelineSourceItem(item) ||
    item.node_type === 'api_target' ||
    (isSecondaryFlow && !expanded) ||
    item.flow_node_id == null
  ) {
    return;
  }

  const flowData = buildMergedFlowDataForSchedule(item, entities?.flowNodes, entities?.sources);
  const { shouldRenderCron } = getScheduleCanvasVisibility(
    flowData,
    vendors,
    metaInfoById,
    undefined,
    byoV2Enabled,
  );
  if (!shouldRenderCron) {
    return;
  }

  const graphKey = getScheduleCanvasGraphKey(item, scheduleCanvasFlowRootId);
  const cronNodeId = `cron-${graphKey}`;
  const targetId = `node-${item.flow_node_id}`;
  const edgeId = `flow-control-edge-${cronNodeId}`;
  const sourceHandle = layoutDirection === 'TB' ? 'bottom' : 'right';

  pushScheduleCanvasFlowEdge(edges, {
    edgeId,
    sourceId: cronNodeId,
    targetId,
    sourceHandle,
    scheduleCanvasEdgeIds,
    existingNodeIds,
    isSecondaryFlow,
    expanded,
  });
}

function createCredentialEdge(
  item,
  edges,
  { expandedNodes, isSecondaryFlow, credentialEdgeIds, existingNodeIds = new Set<string>() },
) {
  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));
  if (
    !isPipelineSourceItem(item) ||
    item.node_type === 'api_target' ||
    (isSecondaryFlow && !expanded) ||
    (!item.data_credentials_id && !item.data_credentials_group_id)
  ) {
    return;
  }

  const isGroup = !!item.data_credentials_group_id;
  const edgeId = isGroup
    ? `edge-credential-group-${item.flow_node_id}`
    : `edge-credential-${item.flow_node_id}`;
  const sourceId = `credential-${item.flow_node_id}`;
  const targetId = `node-${item.flow_node_id}`;
  if (credentialEdgeIds[edgeId]) {
    return;
  }

  if (
    existingNodeIds.size > 0 &&
    (!existingNodeIds.has(sourceId) || !existingNodeIds.has(targetId))
  ) {
    return;
  }

  const edge = {
    id: edgeId,
    source: sourceId,
    target: targetId,
    animated: false,
    targetHandle: 'credential-in',
    sourceHandle: 'credential-out',
    zIndex: isSecondaryFlow && expanded ? 3001 : 1,
    selectable: false,
    deletable: false,
    focusable: false,
    reconnectable: false,
    style: { stroke: 'var(--color-greyscale-600)' },
    markerEnd: {
      type: 'arrow',
      color: 'var(--color-greyscale-600)',
      strokeWidth: 2,
    },
  } as Edge;

  edges.push(edge);
  credentialEdgeIds[edgeId] = true;
}

// Connects a BYO (custom) flow's source node to its synthetic Code node.
function createByoCodeSinkEdges(
  item,
  edges,
  filteredPipelineItems,
  { expandedNodes, isSecondaryFlow, existingNodeIds = new Set<string>(), byoV2Enabled = false },
) {
  if (item.type !== 'SOURCE' || item.flow_type !== FLOW_TYPE_CUSTOM || !byoV2Enabled) return;
  if (!item.children?.length) return;

  const codeNodeId = 'code-' + item.flow_node_id;
  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));

  // SOURCE.children → detected nexset nodeKeys (hidden for BYO — the Code
  // node stands in for it); its children are DATA_SINKs and/or transformed
  // DATA_SETs. Both hang off the Code node, or a transformed nexset would
  // render with no incoming edge.
  const sinks: any[] = [];
  for (const nexsetKey of item.children as any[]) {
    const nexset = filteredPipelineItems[nexsetKey];
    if (!nexset?.children?.length) continue;
    for (const sinkKey of nexset.children as any[]) {
      const sink = filteredPipelineItems[sinkKey];
      if (sink?.type === 'DATA_SINK' || sink?.type === 'DATA_SET') sinks.push(sink);
    }
  }

  sinks.forEach((sink: any) => {
    const targetNodeId = 'node-' + sink.flow_node_id;
    const edgeId = `edge-byo-code-sink-${item.flow_node_id}-${sink.flow_node_id}`;
    if (edges.some((e) => e.id === edgeId)) return;

    if (existingNodeIds.size > 0) {
      if (!existingNodeIds.has(codeNodeId) || !existingNodeIds.has(targetNodeId)) return;
    }

    edges.push({
      id: edgeId,
      source: codeNodeId,
      target: targetNodeId,
      selectable: false,
      deletable: false,
      focusable: false,
      reconnectable: false,
      zIndex: isSecondaryFlow && expanded ? 3001 : 1,
      markerEnd: {
        type: MarkerType.Arrow,
        color: getColorFromItemStatus('ACTIVE'),
        strokeWidth: 2,
      },
      targetHandle: 'left',
      sourceHandle: 'right',
    } as Edge);
  });
}

function createCodeNodeEdge(
  item,
  edges,
  { expandedNodes, isSecondaryFlow, existingNodeIds = new Set<string>(), byoV2Enabled = false },
) {
  if (item.type !== 'SOURCE' || item.flow_type !== FLOW_TYPE_CUSTOM || !byoV2Enabled) {
    return;
  }

  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));
  const sourceId = 'node-' + item.flow_node_id;
  const targetId = 'code-' + item.flow_node_id;
  const edgeId = `edge-code-${item.flow_node_id}`;

  if (edges.some((e) => e.id === edgeId)) {
    return;
  }
  if (
    existingNodeIds.size > 0 &&
    (!existingNodeIds.has(sourceId) || !existingNodeIds.has(targetId))
  ) {
    return;
  }

  edges.push({
    id: edgeId,
    source: sourceId,
    target: targetId,
    selectable: false,
    deletable: false,
    focusable: false,
    reconnectable: false,
    zIndex: isSecondaryFlow && expanded ? 3001 : 1,
    markerEnd: {
      type: MarkerType.Arrow,
      color: getColorFromItemStatus('ACTIVE'),
      strokeWidth: 2,
    },
    targetHandle: 'left',
    sourceHandle: 'right',
  } as Edge);
}

// Draws a Code→DestShell edge when a BYO source has no sink attached yet.
function createByoCodeDestShellEdge(
  item,
  edges,
  filteredPipelineItems,
  { expandedNodes, isSecondaryFlow, existingNodeIds = new Set<string>(), byoV2Enabled = false },
) {
  if (item.type !== 'SOURCE' || item.flow_type !== FLOW_TYPE_CUSTOM || !byoV2Enabled) return;

  // Skip if any actual sink already exists in this flow — same check the
  // mapper uses to decide whether to render the dest shell at all.
  if (hasDescendantSink(item, filteredPipelineItems)) return;

  const codeNodeId = 'code-' + item.flow_node_id;
  const destShellId = 'byo-dest-shell-' + item.flow_node_id;
  const edgeId = `edge-byo-dest-shell-${item.flow_node_id}`;

  if (edges.some((e) => e.id === edgeId)) return;
  if (
    existingNodeIds.size > 0 &&
    (!existingNodeIds.has(codeNodeId) || !existingNodeIds.has(destShellId))
  )
    return;

  const expanded = expandedNodes.includes('flow-' + (item.origin_node_id || item.id));

  edges.push({
    id: edgeId,
    source: codeNodeId,
    target: destShellId,
    selectable: false,
    deletable: false,
    focusable: false,
    reconnectable: false,
    zIndex: isSecondaryFlow && expanded ? 3001 : 1,
    markerEnd: {
      type: MarkerType.Arrow,
      color: getColorFromItemStatus('ACTIVE'),
      strokeWidth: 2,
    },
    targetHandle: 'left',
    sourceHandle: 'right',
  } as Edge);
}

// Function to check for connection of triggering flows
function checkForTriggeringFlows(item, edges, { nodeProps }) {
  if (item.flow_triggers) {
    item.flow_triggers.forEach((trigger: any) => {
      if (item.type !== 'SOURCE') return;

      if (item.id === trigger.id) return;

      const edgeStatus = 'DEPENDENCY';
      const edgeLabel = `Triggered by ${resourceNameTitled(
        trigger.triggering_resource_type,
      )} ${trigger.triggering_resource_id}`;

      const expanded = nodeProps?.expandedNodes?.includes(
        'flow-' + (item.origin_node_id || item.id),
      );

      const edge = {
        id: `edge-flowtrigger-${item?.flow_node_id}-${trigger?.flow_node_id}`,
        className: 'edge-status-' + edgeStatus,
        source: `flow-${item.flow_node_id}`,
        target: `flow-${trigger?.flow_node_id}`,
        selectable: false,
        deletable: false,
        focusable: false,
        connectable: false,
        reconnectable: false,
        label: edgeLabel,
        labelShowBg: true,
        labelBgBorderRadius: 4,
        labelBgPadding: [4, 4],
        labelBgStyle: labelBgStyles[edgeStatus],
        labelStyle: labelStyles[edgeStatus],
        zIndex: expanded ? 3002 : undefined,
        markerEnd: {
          type: MarkerType.Arrow,
          color: getColorFromItemStatus(edgeStatus),
          strokeWidth: 2,
        },
        type: 'curved',
        targetHandle: 'target-flow',
        sourceHandle: 'source-flow',
      };

      if (!edges.some((e) => e.id === edge.id)) {
        edges.push(edge);
      }
    });
  }
}

// This function create edges for flows that are linked
function checkForFlowLinks(
  item,
  edges,
  { nodeProps, metaInfoById, entities, edgesLinkMap },
): Edge[] {
  if (item.linked_flows) {
    item.linked_flows.forEach((linkFlowId: any) => {
      const fromFlowId = item.flow_node_id ?? item.id;
      if (fromFlowId == null || fromFlowId === '' || linkFlowId == null || linkFlowId === '') {
        return;
      }
      // key is created this way, because the two flows, have the linked_flows property.
      // I'm doing this Math.min and max here to avoid different keys for the same connection
      const key = `${Math.min(fromFlowId, linkFlowId)}-${Math.max(fromFlowId, linkFlowId)}`;
      // Checking if edgesLinksMap has already this key, so we don't create duplicates
      if (edgesLinkMap[key]) return;
      edgesLinkMap[key] = true;
      const edgeStatus = 'RELATION';
      const edgeLabel = item?.flow_type === 'rag' ? 'Retriever' : 'Related';

      const source: any =
        item.type === 'SOURCE'
          ? item
          : Object.values(item.items).find(
              (i: any) => i.type === 'SOURCE' && i.id === item.data_source_id,
            );

      const expanded = nodeProps?.expandedNodes?.includes(
        'flow-' + (item.origin_node_id || item.id),
      );

      const edge = {
        id: `edge-flowlink-${source?.flow_node_id ?? fromFlowId}-${linkFlowId}`,
        className: 'edge-status-' + edgeStatus,
        source: `flow-${fromFlowId}`,
        target: `flow-${linkFlowId}`,
        selectable: false,
        deletable: false,
        focusable: false,
        connectable: false,
        reconnectable: false,
        label: edgeLabel,
        labelShowBg: true,
        labelBgBorderRadius: 4,
        labelBgPadding: [4, 4],
        labelBgStyle: labelBgStyles[edgeStatus],
        labelStyle: labelStyles[edgeStatus],
        zIndex: expanded ? 3001 : undefined,
        type: 'curved',
        targetHandle: 'target-flow',
        sourceHandle: 'source-flow',
      };
      edges.push(edge);
    });
  }
  return edges;
}
